import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Chip } from 'components';
import { routes } from 'constants/index';

import { useCargoClaimHistoryQuery, useCargoClaimQuery } from '../queries';
import { toCargoClaimDetail } from '../toCargoClaim';
import { statusLabel, statusColor } from '../statuses';
import '../cargo-claims.scss';

const DOCUMENT_LABELS: Record<string, string> = {
  INVOICE: 'Invoice',
  PACKING_LIST: 'Packing list',
  AWB_BL: 'AWB / BL',
  CUSTOMS: 'Customs clearing papers',
  POD: 'Delivery receipt',
  BOQ: 'BOQ',
  OTHER: 'Other',
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="cargo-claim-details__label">{label}</span>
      <span className="cargo-claim-details__value">{value}</span>
    </div>
  );
}

export default function CargoClaimDetails() {
  const { reference } = useParams();

  const claimQuery = useCargoClaimQuery(reference);
  const historyQuery = useCargoClaimHistoryQuery(reference);

  /*
   * Both reads are scoped by the service against the caller's policy holders,
   * and a claim outside them answers 404 — so "not found" here covers both a
   * reference that does not exist and one that is not this caller's, which is
   * deliberate: references are sequential and telling the two apart would
   * confirm which ones are real.
   */
  const claim = useMemo(() => {
    const payload = claimQuery.data?.data.data;

    if (!payload) return undefined;

    return toCargoClaimDetail(
      payload.claim,
      payload.lines,
      payload.documents,
      historyQuery.data?.data.data ?? []
    );
  }, [claimQuery.data, historyQuery.data]);

  const status = claim?.status ?? 'RECEIVED';
  const history = claim?.history ?? [];

  if (claimQuery.isLoading) {
    return (
      <div className="cargo-claim-details">
        <Link className="cargo-claim-details__back" to={routes.cargoClaims.index}>
          Back to claims
        </Link>
        <p className="cargo-claim-details__empty">Loading…</p>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="cargo-claim-details">
        <Link className="cargo-claim-details__back" to={routes.cargoClaims.index}>
          Back to claims
        </Link>
        <p className="cargo-claim-details__empty">
          No claim found for reference {reference}.
        </p>
      </div>
    );
  }


  return (
    <div className="cargo-claim-details">
      <Link className="cargo-claim-details__back" to={routes.cargoClaims.index}>
        Back to claims
      </Link>

      <div className="cargo-claim-details__header">
        <div>
          <h1 className="cargo-claim-details__reference">{claim.reference}</h1>
          <p className="cargo-claim-details__summary">
            {claim.brand} · Shipment {claim.shipmentNumber} · HAWB{' '}
            {claim.hawbNumber} · {claim.amount.toLocaleString()}{' '}
            {claim.currency}
          </p>
          <div style={{ marginTop: '1rem' }}>
            <Chip
              label={statusLabel(status)}
              color={statusColor(status)}
            />
          </div>
        </div>

      </div>

      <div className="cargo-claim-details__panel">
        <h2 className="cargo-claim-details__panel-title">Claim</h2>
        <div className="cargo-claim-details__grid">
          <Field label="Company" value={claim.company} />
          <Field label="Brand" value={claim.brand} />
          <Field label="Shipment #" value={claim.shipmentNumber} />
          <Field label="HAWB #" value={claim.hawbNumber} />
          <Field label="Nature of loss" value={claim.natureOfLoss} />
          <Field label="Current location" value={claim.currentLocation} />
          <Field
            label="Amount"
            value={`${claim.amount.toLocaleString()} ${claim.currency}`}
          />
          <Field label="Claim date" value={claim.claimDate} />
          <Field label="Notified" value={claim.notifyingDate} />
          <Field label="Contact person" value={claim.contactPerson} />
          <Field
            label="Source"
            value={claim.source === 'DR' ? 'DR module' : 'Manual'}
          />
          <Field label="Eligible" value={claim.eligible ? 'Yes' : 'No'} />
          {/*
            Whether the company code matched one of Clover's policy holders.
            Shown because an unattributed claim is visible to whoever holds
            `Others` rather than to the client it belongs to, and nothing else
            on the screen says so.
          */}
          <Field
            label="Company recognised"
            value={claim.companyRecognised === false ? 'No' : 'Yes'}
          />
        </div>
      </div>

      {/*
        Only once one exists. An empty credit note panel on a claim that has
        not been settled reads as a claim that was settled for nothing.
      */}
      {claim.creditNote && (
        <div className="cargo-claim-details__panel">
          <h2 className="cargo-claim-details__panel-title">Credit note</h2>
          <div className="cargo-claim-details__grid">
            <Field label="Invoice #" value={claim.creditNote.invoiceNumber} />
            <Field label="Invoice date" value={claim.creditNote.date} />
            <Field
              label="Amount"
              value={`${claim.creditNote.amount.toLocaleString()} ${
                claim.creditNote.currency
              }`}
            />
          </div>
        </div>
      )}

      <div className="cargo-claim-details__panel">
        <h2 className="cargo-claim-details__panel-title">
          Lines ({claim.lines.length})
        </h2>
        {claim.lines.length === 0 ? (
          <p className="cargo-claim-details__empty">
            No lines received yet. AZADEA appends them as each business unit
            completes its DR.
          </p>
        ) : (
          <div className="cargo-claim-details__grid">
            {claim.lines.map((line) => (
              <div key={`${line.businessUnit}-${line.description}`}>
                <span className="cargo-claim-details__label">
                  {line.businessUnit} · qty {line.quantity}
                </span>
                <span className="cargo-claim-details__value">
                  {line.description}, {line.fobAmount.toLocaleString()}{' '}
                  {line.currency}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="cargo-claim-details__panel">
        <h2 className="cargo-claim-details__panel-title">
          Documents ({claim.documents.length})
        </h2>
        {claim.documents.length === 0 ? (
          <p className="cargo-claim-details__empty">
            No documents yet. AZADEA has three months from notification to
            upload them.
          </p>
        ) : (
          <div className="cargo-claim-details__grid">
            {claim.documents.map((document) => (
              <div key={document.id}>
                <span className="cargo-claim-details__label">
                  {DOCUMENT_LABELS[document.type]} · {document.sizeKb} KB
                </span>
                <span className="cargo-claim-details__value">
                  {document.fileName}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="cargo-claim-details__panel">
        <h2 className="cargo-claim-details__panel-title">History</h2>
        <ul className="cargo-claim-details__history">
          {history.map((entry, index) => (
            <li
              className="cargo-claim-details__history-item"
              key={`${entry.to}-${entry.at}-${index}`}
            >
              <span className="cargo-claim-details__history-when">
                {entry.at}
              </span>
              <span>
                <span className="cargo-claim-details__history-what">
                  {entry.from
                    ? `${statusLabel(entry.from)} → ${statusLabel(entry.to)}`
                    : statusLabel(entry.to)}
                  {entry.reason ? `. ${entry.reason}` : ''}
                </span>
                <span className="cargo-claim-details__history-who">
                  {entry.actor} ({entry.actorType.toLowerCase()})
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
